import { useEffect, useState } from "react";
import { CheckCircle2, XCircle, Loader2, AlertTriangle, FileText } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";

/**
 * Aug-2026 (User-Wunsch) — Prüf-Freigabe für den Filialleiter:
 * Zeigt das Gesamt-PDF (Kaufvertrag + AGB + Datenschutz + alle Belege) zum
 * Durchscrollen. „In Ordnung" gibt frei, „Fehlerhaft" öffnet ein Pflichtfeld
 * für den Ablehnungsgrund, über den der Verkäufer informiert wird.
 */
export default function DealApprovalReviewModal({ task, onClose, onDone }) {
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfError, setPdfError] = useState(null);
    const [busy, setBusy] = useState(false);
    const [rejecting, setRejecting] = useState(false);
    const [reason, setReason] = useState("");

    useEffect(() => {
        let objectUrl = null;
        let cancelled = false;
        api.get(`/listings/${task.listing_id}/dispo/documents-bundle.pdf?include_uploads=1`, { responseType: "blob" })
            .then((r) => {
                if (cancelled) return;
                objectUrl = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
                setPdfUrl(objectUrl);
            })
            .catch(async (e) => {
                if (cancelled) return;
                let msg = "Gesamt-PDF konnte nicht geladen werden";
                try { msg = JSON.parse(await e.response.data.text())?.detail || msg; } catch { /* noop */ }
                setPdfError(msg);
            });
        return () => { cancelled = true; if (objectUrl) URL.revokeObjectURL(objectUrl); };
    }, [task.listing_id]);

    const approve = async () => {
        setBusy(true);
        try {
            await api.post(`/me/tasks/${task.id}/approve-deal`);
            toast.success("Gesamtgeschäft freigegeben — Auftragsbestätigung & Werkstattauftrag werden erstellt");
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
            setBusy(false);
        }
    };

    const reject = async () => {
        if (!reason.trim()) {
            toast.error("Bitte einen Ablehnungsgrund erfassen — Pflichtfeld.");
            return;
        }
        setBusy(true);
        try {
            await api.post(`/me/tasks/${task.id}/reject-deal`, { reason: reason.trim() });
            toast.success("Als fehlerhaft abgelehnt — der Verkäufer wurde mit dem Grund informiert");
            onDone?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Ablehnung fehlgeschlagen");
            setBusy(false);
        }
    };

    return (
        <Dialog open onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="max-w-4xl p-0 gap-0 overflow-hidden" data-testid="deal-review-modal">
                <DialogHeader className="px-4 py-3 border-b border-gray-200">
                    <DialogTitle className="text-base font-black flex items-center gap-2">
                        <FileText className="w-4 h-4" /> Prüf-Freigabe — {task.title?.replace("Gesamtgeschäft-Freigabe ausstehend — ", "") || "Gesamtgeschäft"}
                    </DialogTitle>
                    <p className="text-[11px] text-gray-500">
                        Gesamt-PDF (Kaufvertrag, AGB, Datenschutz &amp; alle hochgeladenen Belege) prüfen, dann freigeben oder als fehlerhaft ablehnen.
                    </p>
                </DialogHeader>
                <div className="bg-gray-100" style={{ height: "58vh" }}>
                    {pdfUrl ? (
                        <iframe src={pdfUrl} title="Gesamt-PDF (Prüfansicht)" className="w-full h-full border-0" data-testid="deal-review-pdf" />
                    ) : pdfError ? (
                        <div className="h-full flex flex-col items-center justify-center gap-2 text-center px-6" data-testid="deal-review-pdf-error">
                            <AlertTriangle className="w-6 h-6 text-amber-500" />
                            <p className="text-sm text-gray-600">{pdfError}</p>
                            <p className="text-[11px] text-gray-400">Die Freigabe/Ablehnung ist trotzdem möglich.</p>
                        </div>
                    ) : (
                        <div className="h-full flex items-center justify-center gap-2 text-gray-500 text-sm" data-testid="deal-review-pdf-loading">
                            <Loader2 className="w-4 h-4 animate-spin" /> Gesamt-PDF wird geladen…
                        </div>
                    )}
                </div>
                <div className="px-4 py-3 border-t border-gray-200 bg-white space-y-2">
                    {rejecting && (
                        <div className="space-y-1.5" data-testid="deal-review-reject-form">
                            <label className="text-[11px] font-bold uppercase tracking-wider text-red-700">
                                Ablehnungsgrund <span title="Pflichtfeld">*</span> — wird dem Verkäufer mitgeteilt
                            </label>
                            <Textarea
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                rows={3}
                                autoFocus
                                placeholder="Was ist fehlerhaft? (z. B. Ausweiskopie unleserlich, Kaufvertrag ohne Unterschrift …)"
                                className="text-sm border-2 border-red-300 focus:border-red-500 bg-red-50/40"
                                data-testid="deal-review-reason"
                            />
                        </div>
                    )}
                    <div className="flex items-center justify-end gap-2 flex-wrap">
                        <Button type="button" variant="outline" size="sm" onClick={onClose} disabled={busy} className="h-9 text-xs" data-testid="deal-review-cancel">
                            Abbrechen
                        </Button>
                        {rejecting ? (
                            <Button type="button" size="sm" onClick={reject} disabled={busy} className="h-9 text-xs bg-red-600 hover:bg-red-700 text-white" data-testid="deal-review-reject-confirm">
                                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><XCircle className="w-4 h-4 mr-1" /> Ablehnung senden</>}
                            </Button>
                        ) : (
                            <Button type="button" size="sm" onClick={() => setRejecting(true)} disabled={busy} variant="outline" className="h-9 text-xs border-2 border-red-300 text-red-700 hover:bg-red-50" data-testid="deal-review-reject">
                                <XCircle className="w-4 h-4 mr-1" /> Fehlerhaft — Ablehnen
                            </Button>
                        )}
                        <Button type="button" size="sm" onClick={approve} disabled={busy || rejecting} className="h-9 text-xs bg-emerald-700 hover:bg-emerald-800 text-white" data-testid="deal-review-approve">
                            {busy && !rejecting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><CheckCircle2 className="w-4 h-4 mr-1" /> In Ordnung — Freigeben</>}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}
